import type { DOMAttributes, DragEvent } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { isWeb } from "tamagui";

export type FileDropHandlers = Pick<
  DOMAttributes<HTMLDivElement>,
  "onDragEnter" | "onDragOver" | "onDragLeave" | "onDrop"
>;

export type UseFileDropResult = {
  isDragActive: boolean;
  dropHandlers: FileDropHandlers;
};

type UseFileDropParams = {
  onFilesDropped: (files: File[]) => void;
  /** Allowed MIME types. */
  accept: string[];
  /** Allowed extensions (lowercased, dot-prefixed); the fallback whenever a
   *  file's MIME type isn't in `accept` — empty (e.g. HEIC) or a generic
   *  `application/octet-stream` the OS hands back for a real PDF. */
  acceptExtensions: string[];
  /** Max files to report from a single drop — the slot's remaining capacity. */
  maxFiles: number;
  disabled?: boolean;
};

// The drop zone honours `accept` itself. A file whose MIME type isn't in the
// allowlist (empty, or a generic application/octet-stream) is matched by
// extension instead of being dropped.
const isAccepted = (
  file: File,
  accept: string[],
  acceptExtensions: string[],
): boolean => {
  if (accept.includes(file.type)) {
    return true;
  }
  const name = file.name.toLowerCase();
  return acceptExtensions.some(ext => name.endsWith(ext));
};

/**
 * Web drag-and-drop for a file-request surface; a true no-op on native (and when
 * disabled). It only reports the dropped files — the caller owns the upload.
 */
export const useFileDrop = ({
  onFilesDropped,
  accept,
  acceptExtensions,
  maxFiles,
  disabled = false,
}: UseFileDropParams): UseFileDropResult => {
  const [isDragActive, setIsDragActive] = useState(false);
  // dragenter/leave fire per child element; a depth counter keeps the active
  // state from flickering as the pointer crosses inner nodes.
  const depth = useRef(0);

  const onDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    depth.current += 1;
    setIsDragActive(true);
  }, []);

  // preventDefault on dragover is mandatory — without it the browser rejects the
  // drop and onDrop never fires.
  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const onDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    depth.current -= 1;
    if (depth.current <= 0) {
      depth.current = 0;
      setIsDragActive(false);
    }
  }, []);

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      depth.current = 0;
      setIsDragActive(false);
      const accepted = Array.from(event.dataTransfer.files).filter(file =>
        isAccepted(file, accept, acceptExtensions),
      );
      // Slice to the slot's remaining capacity so a multi-file drop can't push
      // past maxFiles — the same cap the add button and drop-disable enforce.
      const toReport = accepted.slice(0, maxFiles);
      if (toReport.length === 0) {
        return;
      }
      onFilesDropped(toReport);
    },
    [accept, acceptExtensions, maxFiles, onFilesDropped],
  );

  // A drag in flight when the slot is disabled would otherwise leave the active
  // highlight stuck for the next re-enable; clear the bookkeeping on disable.
  useEffect(() => {
    if (disabled) {
      depth.current = 0;
      setIsDragActive(false);
    }
  }, [disabled]);

  if (!isWeb || disabled) {
    return { isDragActive: false, dropHandlers: {} };
  }

  return {
    isDragActive,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
};
